const translations = {
  en: {
    nav: {
      home: "Home",
      experience: "Experience",
      education: "Education",
      projects: "Projects",
      contact: "Contact",
    },
    settings: {
      language: "Language",
      theme: "Theme",
    },
    greeting: {
      title2: "Taro Shibusawa",
      fullName: "Taro Shibusawa",
      subTitle1: "Principal Software Engineer",
      subTitle2: "AI/ML & GenAI Engineer",
      summary:
        "AI/ML software engineer and Fullstack architect with 8+ years building and leading production-grade AI systems and scalable applications. I close the gap between cutting-edge AI research and tangible business value — owning problems end-to-end, from data pipelines and machine-learning models through to the final user interface, and delivering solutions that drive measurable ROI.",
      resumeBtn: "Resume",
    },
    footer: {
      madeWith: "Made with ❤️",
      beforeAuthor: " by ",
    },
    skills: {
      // header: "Here's what I do",
      categories: [
        {
          title: "AI/ML & GenAI",
          skills: [
            "⚡ Building RAG pipelines, LLM evaluation frameworks, and multi-agent orchestration systems",
            "⚡ Designing real-time voice AI pipelines with ~1s latency using Vapi, Retell AI, and ElevenLabs",
            "⚡ Integrating OpenAI, Anthropic Claude, LangChain, PyTorch, and TensorFlow into production systems",
          ],
        },
        {
          title: "Full Stack Development",
          skills: [
            "⚡ Building scalable full-stack apps with React, Next.js, Node.js, and mobile clients in Flutter & React Native",
            "⚡ Designing REST, GraphQL, and WebSocket APIs for real-time web and mobile data synchronization",
            "⚡ Implementing OAuth2/JWT authentication, CI/CD pipelines, and microservices architecture",
          ],
        },
        {
          title: "Cloud & Data Infrastructure",
          skills: [
            "⚡ Architecting event-driven microservices on AWS (Lambda, ECS, API Gateway, SageMaker, Kinesis, S3)",
            "⚡ Managing containerized deployments with Docker, Kubernetes, and Terraform IaC",
            "⚡ Operating streaming pipelines (Kafka, Kinesis) and vector stores (Pinecone, FAISS) at scale",
          ],
        },
      ],
    },
    experience: {
      title: "Experience",
      // subtitle: "Work and Learn",
      description:
        "8+ years shipping production systems — from real-time GenAI clinical platforms and RAG pipelines to financial SaaS and ML-enabled data infrastructure. I own problems end-to-end: architecture, delivery, and the metrics that prove it worked.",
      entries: [
        {
          title: "Principal Software Engineer",
          description:
            "<li>Architected a multi-tenant GenAI clinical platform supporting 200+ concurrent voice and text AI sessions using event-driven microservices on AWS.</li><li>Built a real-time voice AI pipeline converting continuous clinical conversations into structured medical entities with ~1s latency and 97% extraction accuracy, enabling automated documentation workflows.</li><li>Designed and deployed production RAG systems using LangChain and vector databases (Pinecone/FAISS), improving patient-history summarization accuracy with retrieval evaluation achieving 0.9 precision.</li><li>Developed a modular LLM orchestration layer separating retrieval, prompt execution, and inference services, enabling controlled experimentation and rapid feature delivery.</li><li>Reduced frontend payload size by 40%, maintaining sub-second responsiveness across streaming AI interfaces. Led infrastructure automation with Terraform and GitHub Actions, achieving 98% automated test coverage.</li><li>Mentored 6 engineers across distributed systems design, async Python, and GenAI platform development.</li>",
        },
        {
          title: "Software Engineer",
          description:
            "<li>Developed production full-stack applications using React and Node.js supporting secure customer financial workflows at scale.</li><li>Designed microservices architectures improving system scalability and resilience across distributed services.</li><li>Built REST and WebSocket APIs enabling real-time synchronization of financial data during peak usage.</li><li>Implemented OAuth2 and JWT authentication systems strengthening platform security and compliance.</li><li>Reduced release cycles from weeks to under 24 hours through CI/CD automation using Docker and Jenkins.</li><li>Improved frontend performance by ~30% via lazy loading, caching strategies, and bundle optimization.</li>",
        },
        {
          title: "Data Specialist",
          description:
            "<li>Operated streaming ingestion platforms processing 3,000 events/sec using Kafka, AWS Kinesis, and Azure Event Hubs.</li><li>Maintained centralized PostgreSQL data warehouse with Redis caching achieving ~200ms p95 analytical query latency.</li><li>Developed React + D3.js analytics dashboards enabling 50+ stakeholders to visualize clinical and operational insights.</li><li>Applied Bayesian optimization models improving clinical trial site selection efficiency by ~18%.</li><li>Built predictive models for radiological analysis and equipment failure forecasting, reducing downtime by ~28%.</li>",
        },
        {
          title: "Data Analyst",
          description:
            "<li>Built Fraud Fighter (Python + Node.js + AWS Lambda) that reduced platform abuse by 20% and boosted user engagement by 30% — owned concept, build, and production deployment solo.</li><li>Redesigned denormalized SQL schema on AWS RDS achieving a 6x query performance gain.</li><li>Migrated Backbone.js frontend to React, reducing bundle size and improving long-term maintainability.</li>",
        },
      ],
    },
    education: {
      heading: "Education",
      subHeading: "Basic Qualification and Certifications",
      description:
        "I want to prove my ability through successful work. Below are my major certifications and degree.",
      qualificationHeader: "Qualification",
      certificationsHeader: "Certifications",
      visitWebsite: "Visit Website",
    },
    projects: {
      title: "Projects",
      description:
        "Production systems across AI/CV, SaaS rescue & rebuild, enterprise data sync, and geospatial ML. Every project was shipped end-to-end — architecture, build, and deployment.",
      modalLabels: {
        role: "Role",
        about: "About",
        problem: "Problem",
        solution: "Solution",
        results: "Results",
      },
      descriptions: [
        "World's first AI-powered automated refereeing system for padel. Multi-camera computer vision pipeline — ball tracking, 4-player skeleton detection, and court boundary recognition running at 60fps with sub-16ms decision latency. Validated against a 20-match test dataset at 99.9% accuracy. Solo build: full-stack + CV engineering.",
        "Inherited a broken Amazon monitoring SaaS — stabilised the failing 15-minute scrape cycle in under a week, then rebuilt the entire system from scratch. Per-user job isolation, optimised SQL query patterns, async scraping engine, and alert routing covering price, title, review count, BuyBox status, hijacker detection, and MAP violations. Still live in production.",
        "Full ownership of a subscription SaaS delivering 45+ years of Richardson Estimating Standards pricing data to construction estimators. Migrated a legacy database across US, Canada, and International markets, provisioned all servers, and built a complete SPA with subscriber auth, 15+ category hierarchical navigation, dynamic filterable pricing tables, and an estimating list workflow.",
        "Two-module SaaS for enterprise event teams. Module one: sync engine automating data transfers between Salesforce, RainFocus, and Zoom — eliminating manual CSV uploads. Module two: self-serve analytics dashboard with 8 configurable widget types (donut, bar, gauge, treemap, leaderboard, line item) and real-time event performance tracking across regions and campaigns.",
        "Rescued and rebuilt the #1 rated SMS CRM for real estate professionals. Stabilised a collapsing system immediately, then led a 6-contractor team through a complete ground-up rebuild. Delivers 100k+ SMS/month with 98.1% deliverability. Features a 3-panel prospect CRM with lead temperature scoring, DNC controls, skip tracing, scheduled sends, and a live analytics dashboard. Multi-year engagement.",
        "Full-stack AI music SaaS where users generate original audio samples by configuring genre, BPM, key, duration, and style tags. Built the full AI generation pipeline in Python/PyTorch, a community Explore feed with trending hashtags, creator profiles, sample packs, and a dual monetisation system — credit purchases and 4-tier subscriptions ($0–$49.99/month) with 24-bit WAV export.",
        "End-to-end data science pipeline clustering 100 Toronto neighbourhoods by venue character using K-means. Scraped postal code data from Wikipedia, pulled venue data from the Foursquare API, engineered top-9 venue category features per neighbourhood, applied unsupervised clustering, and visualised results on two interactive Folium maps — raw distribution and colour-coded cluster output.",
      ],
    },
    contact: {
      title: "Contact Me",
      description:
        "You can contact me at the places mentioned below. I will try to get back to you as fast as I can.",
    },
    contactForm: {
      title: "Fill Out the Form",
      subtitle: "I will get back to you within 24 hours",
      name: "Full Name",
      namePlaceholder: "Your name and surname",
      email: "Email",
      emailPlaceholder: "example@email.com",
      phone: "Phone",
      phonePlaceholder: "+1 (XXX) XXX XX XX",
      company: "Company",
      companyPlaceholder: "Your company name",
      service: "Which Service Do You Need?",
      details: "Project Details",
      detailsPlaceholder:
        "Provide detailed information about your project. What do you want to do, what is your budget, what is your timeline?",
      send: "Send",
      sending: "Sending...",
      success: "Message sent! I'll get back to you within 24 hours.",
      error: "Something went wrong. Please try again or email me directly.",
      bookMeeting: "Book a Meeting",
      infoTitle: "Get in Touch With Me",
      infoDesc:
        "Have questions? Reach out through your preferred communication channel. I will try to get back to you as fast as I can.",
      sendEmail: "Send Email",
      connect: "Connect",
      visit: "Visit",
      location: "Remote — Worldwide",
      availabilityLabel: "AVAILABILITY",
      workingDays: "Mon – Fri",
      active: "Active",
    },
  },

  ja: {
    nav: {
      home: "ホーム",
      experience: "職歴",
      education: "学歴",
      projects: "プロジェクト",
      contact: "お問い合わせ",
    },
    settings: {
      language: "言語",
      theme: "テーマ",
    },
    greeting: {
      title: "こんにちは",
      title2: "、渋沢 太郎です",
      fullName: "渋沢 太郎",
      subTitle1: "プリンシパル・エンジニア",
      subTitle2: "AI/ML・生成AIエンジニア",
      summary:
        "8年以上にわたり本番レベルのAIシステムとスケーラブルなアプリケーションを設計・主導してきたAI/MLソフトウェアエンジニア／フルスタックアーキテクトです。最先端のAI研究とビジネス価値のギャップを埋め、データパイプライン・機械学習モデルから最終UIまで一気通貫でオーナーシップを持ち、測定可能なROIを生むソリューションを届けます。",
      resumeBtn: "履歴書",
    },
    footer: {
      madeWith: "❤️を込めて制作",
      beforeAuthor: " ",
    },
    skills: {
      header: "スキル・専門分野",
      categories: [
        {
          title: "AI/ML・生成AI",
          skills: [
            "⚡ RAGパイプライン・LLM評価フレームワーク・マルチエージェントオーケストレーションの構築",
            "⚡ Vapi・Retell AI・ElevenLabsを使った~1秒レイテンシのリアルタイム音声AIパイプラインの設計",
            "⚡ OpenAI・Anthropic Claude・LangChain・PyTorch・TensorFlowを本番システムへ統合",
          ],
        },
        {
          title: "フルスタック開発",
          skills: [
            "⚡ React・Next.js・Node.js、およびFlutter・React Nativeによるモバイルクライアントを含むスケーラブルなフルスタックアプリの構築",
            "⚡ Web・モバイル向けリアルタイムデータ同期のためのREST・GraphQL・WebSocket APIの設計",
            "⚡ OAuth2/JWT認証・CI/CDパイプライン・マイクロサービスアーキテクチャの実装",
          ],
        },
        {
          title: "クラウド・データインフラ",
          skills: [
            "⚡ AWS（Lambda・ECS・API Gateway・SageMaker・Kinesis・S3）上のイベント駆動マイクロサービスの設計",
            "⚡ Docker・Kubernetes・Terraform IaCによるコンテナデプロイの管理",
            "⚡ Kafka・Kinesisストリーミングパイプラインとベクトルストア（Pinecone・FAISS）の大規模運用",
          ],
        },
      ],
    },
    experience: {
      title: "職歴",
      subtitle: "キャリアと学び",
      description:
        "8年以上にわたり本番システムを開発・納品してきました。リアルタイム生成AIプラットフォーム・RAGパイプラインから金融SaaS・MLデータインフラまで、アーキテクチャから納品・成果の証明まで一気通貫でオーナーシップを持って取り組んでいます。",
      entries: [
        {
          title: "プリンシパルソフトウェアエンジニア",
          description:
            "<li>AWSのイベント駆動マイクロサービスを使って200以上の同時音声・テキストAIセッションをサポートするマルチテナント生成AI臨床プラットフォームを設計。</li><li>継続的な臨床会話を~1秒レイテンシ・97%の抽出精度で構造化医療エンティティに変換するリアルタイム音声AIパイプラインを構築。自動文書化ワークフローを実現。</li><li>LangChainとベクトルデータベース（Pinecone/FAISS）を使ったRAGシステムを本番導入。患者履歴サマリーの精度を向上させ、検索評価で0.9の精度を達成。</li><li>検索・プロンプト実行・推論サービスを分離したモジュラーLLMオーケストレーション層を開発。実験管理と迅速な機能提供を実現。</li><li>フロントエンドのペイロードサイズを40%削減し、ストリーミングAIインターフェースでのサブ秒応答を維持。TerraformとGitHub ActionsでIaC自動化を主導し、自動テストカバレッジ98%を達成。</li><li>分散システム設計・非同期Python・生成AIプラットフォーム開発にわたる6名のエンジニアをメンタリング。</li>",
        },
        {
          title: "ソフトウェアエンジニア",
          description:
            "<li>ReactとNode.jsを使って、大規模な安全な顧客金融ワークフローをサポートする本番フルスタックアプリを開発。</li><li>分散サービス全体のシステムスケーラビリティと回復力を向上するマイクロサービスアーキテクチャを設計。</li><li>ピーク時における金融データのリアルタイム同期を可能にするRESTおよびWebSocket APIを構築。</li><li>OAuth2およびJWT認証システムを実装し、プラットフォームのセキュリティとコンプライアンスを強化。</li><li>DockerとJenkinsによるCI/CD自動化でリリースサイクルを数週間から24時間以内に短縮。</li><li>遅延ロード・キャッシュ戦略・バンドル最適化によりフロントエンドパフォーマンスを~30%改善。</li>",
        },
        {
          title: "データスペシャリスト",
          description:
            "<li>Kafka・AWS Kinesis・Azure Event Hubsを使って毎秒3,000イベントを処理するストリーミング取り込みプラットフォームを運用。</li><li>RedisキャッシュによるPostgreSQLデータウェアハウスを維持し、分析クエリのp95レイテンシ~200msを実現。</li><li>React + D3.jsによる分析ダッシュボードを開発し、50名以上のステークホルダーが臨床・運営データを可視化できる環境を提供。</li><li>ベイズ最適化モデルを適用し、臨床試験サイト選定の効率を~18%改善。</li><li>放射線解析と機器故障予測の予測モデルを構築し、ダウンタイムを~28%削減。</li>",
        },
        {
          title: "データアナリスト",
          description:
            "<li>Fraud Fighter（Python + Node.js + AWS Lambda）を構築し、プラットフォームの不正利用を20%削減・ユーザーエンゲージメントを30%向上。コンセプト・構築・本番デプロイをソロで担当。</li><li>AWS RDS上の非正規化SQLスキーマを再設計し、クエリパフォーマンスを6倍改善。</li><li>Backbone.jsフロントエンドをReactに移行し、バンドルサイズを削減して長期的なメンテナビリティを向上。</li>",
        },
      ],
    },
    education: {
      heading: "学歴",
      subHeading: "学位と資格",
      description:
        "実績を通じてスキルを証明することを重視しています。以下は主な学位と資格です。",
      qualificationHeader: "学位",
      certificationsHeader: "資格・認定",
      visitWebsite: "ウェブサイトへ",
      degrees: [
        {
          title: "大阪大学（大阪府・日本）",
          subtitle: "理学部学士（数学・統計学）",
          duration: "2008年4月～2012年9月",
          descriptions: [
            "⚡ 確率・統計・離散数学の基礎を固め、MLシステムやデータに基づくエンジニアリング判断の土台としています。",
          ],
          alt_name: "大阪大学",
          website_link: "https://www.osaka-u.ac.jp/ja",
        },
      ],
    },
    projects: {
      title: "プロジェクト",
      description:
        "AI/CV・SaaS再構築・エンタープライズデータ同期・地理空間MLにまたがる本番システム。アーキテクチャから構築・デプロイまで、すべてエンドツーエンドで納品しています。",
      modalLabels: {
        role: "役割",
        about: "概要",
        problem: "課題",
        solution: "解決策",
        results: "成果",
      },
      details: [
        {
          role: "ソロ開発 — フルスタック + CVエンジニアリング",
          about:
            "パデル競技向け世界初のAI自動審判システムのWebプラットフォームを構築。マルチカメラコンピュータビジョンとリアルタイム判定エンジンを統合 — ボール追跡・4プレイヤー骨格検出・コート境界認識を60fps・16ms以下の判定レイテンシで実現。",
          problem:
            "人間の審判は高速なボール動作を見逃したり、複数カメラで4プレイヤーを同時追跡できない。審判と試合分析を1つのリアルタイムシステムに統合した製品は存在しなかった。",
          solution:
            "多段CVパイプラインを構築：高速ボール検出 → 信頼度スコアリング付きプレイヤー追跡（0.79〜0.88）→ コート境界認識 → ルールエンジン → WebSocketでReactダッシュボードへ判定をストリーミング。フレーム間のオブジェクト再識別を使った段階的パイプラインで、高速オブジェクト検出と複数プレイヤーの遮蔽問題を解決。",
          done: [
            "8台のカメラで60fpsにて4プレイヤーとボールを同時追跡",
            "アウト判定とルール判断を16ms以内にダッシュボードへ配信",
            "コート境界を人手不要で自動認識",
            "20試合のテストデータセットで99.9%の精度を検証",
          ],
        },
        {
          role: "単独開発者 — フル診断・安定化・再構築",
          about:
            "AMZAlertはAmazon商品データを15分ごとにスクレイピングし、価格・タイトル・レビュー・乗っ取り・MAP違反の変化をSMS/メールでアラート通知するライブサブスクリプションSaaS。壊れたコードベースを引き継ぎ、1週間以内に安定化させ、システム全体をゼロから再構築。現在も本番稼働中。",
          problem:
            "前任開発者のアーキテクチャは共有グローバルキューを使用 — 新規サブスクライバーが増えるたびに既存ユーザーのパフォーマンスが低下。50ユーザー時点で15分スクレイプが2時間以上かかり、顧客が離脱し返金対応が発生。スケールへの道筋がなく完全な再構築が必要だった。",
          solution:
            "初日に共有キューのボトルネックを診断。1週間以内にクリティカルなスクレイプパスにパッチを当て15分サイクルを復旧。その後ゼロから代替システムを設計・構築：ユーザー別ジョブ分離・最適化SQLクエリ・非同期スクレイピングエンジン・価格/タイトル/レビュー数/BuyBox/乗っ取り検知/MAP違反をカバーするアラートルーティング層。",
          done: [
            "全ユーザー同時接続で15分Amazonスクレイプサイクルが安定稼働",
            "全追跡イベントタイプでSMS/メールアラートが正確に発火",
            "サブスクライバー数増加による共有キュー劣化なし",
            "システムの安定稼働と本番運用を確認",
          ],
        },
        {
          role: "単独開発者 — データベース移行・API・SPA・サーバーインフラ全体を担当",
          about:
            "CDOL（Cost Data On Line）は建設見積担当者に膨大なプロプライエタリ価格データベース（資材費・労務費・プロセスプラント仕様・国際コスト係数）へのアクセスを提供するサブスクリプションSaaSで、45年以上のRichardson見積基準に基づいている。",
          problem:
            "クライアントは数十年分のプロプライエタリ建設価格データをレガシー形式で保有しており、Webでの配信手段がなかった。データセット全体を変換・再構築してサブスクリプションゲートの背後に配置し、市場別フィルタリング・階層ナビゲーション・見積ワークフローを実装する必要があった。",
          solution:
            "レガシー価格データベース全体を米国・カナダ・国際市場の3リージョン対応の新スキーマへ移行。Web・API・DBサーバーを構築・管理。サブスクライバー認証・市場セレクタ・15以上のカテゴリ階層ナビ（多段ドリルダウン）・動的フィルタリング価格表・マテリアル詳細モーダル付き見積リストワークフローを備えた完全SPAを構築。",
          done: [
            "レガシーデータセット全体をデータ損失なしで移行",
            "全6製品モジュール（市場レポート・労務データ・コストファクターマニュアル・プロセスプラントデータ等）を本番リリース",
            "サブスクライバー認証・市場別フィルタリング・見積リストワークフロー全て機能",
            "Web・API・DBサーバーの構築とデプロイ完了",
            "管理者ユーザー管理機能を有効化",
          ],
        },
        {
          role: "フルスタック開発者 — 同期エンジンと分析モジュール全体を単独で担当",
          about:
            "Salesforce・RainFocus・Zoom上でイベントを運営するエンタープライズ向けに構築した2モジュールSaaSプラットフォーム。モジュール1はCRMとイベントツール間のデータ同期を自動化（手動CSVアップロード不要）。モジュール2は8種類のウィジェットタイプとリアルタイム可視化を備えたセルフサービス型分析ダッシュボード。",
          problem:
            "エンタープライズ向けイベントチームはプラットフォーム間の参加者データを手動でアップロードしていた — データエラー・報告遅延・不正確なSalesforceキャンペーン指標・ライブイベントのリアルタイム可視性ゼロという問題が発生。",
          solution:
            "Salesforce・RainFocus・Zoom間の転送・更新・削除をイベント別キャンペーンIDリンク付きで自動化する同期エンジンを構築。8種類の自己設定可能なウィジェット（ドーナツ・棒グラフ・折れ線・ゲージ・テーブル・ツリーマップ・リーダーボード・明細）を持つ完全分析モジュールを構築。",
          done: [
            "Salesforce同期を手動データ入力なしで自動化",
            "参加者の更新と削除をリアルタイムで伝播",
            "8種類の設定可能なウィジェットタイプで分析ダッシュボードを稼働",
            "ワークフロー・リージョン・キャンペーン別にイベントパフォーマンスを追跡可能",
            "リリース後のデータ入力エラーゼロをクライアントが確認",
          ],
        },
        {
          role: "リードエンジニア — 6名のサブコントラクターを管理、安定化・再構築・マルチイヤー成長全体を担当",
          about:
            "LaunchControlは不動産プロフェッショナル向けのテキストメッセージプラットフォームで業界1位評価 — 月100k以上のプロスペクトメッセージをハンドリングするフルCRM + SMSエンジン。元の開発チームが解雇された後に参画し、障害発生中のシステムを安定化させ、6名のサブコントラクターチームで完全な再構築を主導。マルチイヤー案件で現在も稼働・成長中。",
          problem:
            "オリジナル開発者がスケールできないシステムを構築した。実際のユーザーが来た途端にパフォーマンスが崩壊 — 顧客は不満を持ち返金を要求し、クライアントは収益を失っていた。アーキテクチャには完全に捨てる以外に成長への道筋がなかった。",
          solution:
            "既存システムを即座に安定化させて出血を止めた。カスタムフレームワークでの完全再構築を提案し主導。6名のサブコントラクターによる再構築と数年間の継続的機能開発を指揮。SMSスレッドビュー・リードスコアリング・DNC管理・スキップトレース統合・予約送信・ライブ分析ダッシュボードを備えた3パネルプロスペクトCRMを納品。",
          done: [
            "プラットフォームをゼロから再構築 — 月100k以上のSMSを劣化なしでハンドリング",
            "DNC・スキップトレース・リードスコアリング・予約送信を備えた3パネルCRMワークフローを稼働",
            "大規模での98.1% SMS配信率を維持",
            "配信率とリードパイプラインを追跡する分析ダッシュボードを稼働",
            "クライアント自称：業界1位評価かつ最高コンバージョン率",
          ],
        },
        {
          role: "AI + フルスタック開発者 — モデル・プラットフォーム・収益化全体をエンドツーエンドで担当",
          about:
            "ooVooはユーザーがジャンル・BPM・キー・デュレーション・最大3つのスタイルタグを設定してオリジナル音源を生成できるフルスタックAI音楽SaaS。生成されたサンプルはコミュニティExploreフィードに公開され、個人ライブラリやパックで管理され、クレジットと4段階の月間サブスクリプションで収益化される。",
          problem:
            "音楽制作ツールは非音楽家には難しすぎるか、汎用的でカスタマイズ不可能な出力を生成する。シリアスなプロデューサーも満足できるクリエイティブコントロールを持つアクセスしやすいAIジェネレーターと、オーガニックなコンテンツ成長とリテンションを促進するコミュニティ層が求められた。",
          solution:
            "Python/PyTorchでジャンル固有の出力にモデルチューニングしたAI生成パイプラインを構築し、構造化された生成フォームを持つNext.js/Reactフロントエンドに接続。完全なコミュニティ層（Exploreフィード・クリエイタープロフィール・フォロワー・サンプルパック・トレンドハッシュタグ）・デュアル収益化システム（クレジット + 4段階サブスクリプション）・ティア別WAVエクスポートを構築。",
          done: [
            "15以上のスタイルパラメーターでブラウザ内再生可能な音声を生成するAIジェネレーターを稼働",
            "トレンドハッシュタグと検索可能なサンプルを持つコミュニティExploreフィードを稼働",
            "フォロワー数・公開サンプルテーブル・パック管理を持つクリエイタープロフィール",
            "クレジットシステムと4段階サブスクリプション課金の両方が稼働中（$0〜$49.99/月）",
            "有料ティアで24-bit WAVダウンロードが利用可能",
          ],
        },
        {
          role: "単独データサイエンティスト — スクレイピングからインタラクティブ地理空間可視化まで全パイプラインを担当",
          about:
            "K-meansを使ってトロント100地区を会場特性でクラスタリングするエンドツーエンドのデータサイエンスパイプライン。Wikipediaから郵便番号データをスクレイピング・Foursquare APIから会場データを取得・地区ごとの上位9会場カテゴリの特徴量を設計し、2種類のインタラクティブFoliumマップで結果を可視化。",
          problem:
            "トロントの100以上の郵便地区には機械可読な類似度インデックスがない。実際の会場特性（地理だけでなく）で地区をグループ化するには、Webスクレイピング・APIデータ・特徴量エンジニアリング・教師なしMLを組み合わせたクリーンで再現可能なパイプラインが必要。",
          solution:
            "WikipediaのトロントZIPコード表をスクレイピング・クリーニング。CSVから座標をマージ。Foursquare Places APIで地区別の会場データを取得。会場カテゴリをワンホットエンコードし地区ごとの上位9会場を順位付け、K-meansクラスタリングで5つの地理的に整合したクラスターを生成。クラスタリング前後をカラーコード付きマーカーのインタラクティブFoliumマップで可視化。",
          done: [
            "100地区のスクレイピング・クリーニング・ジオコーディング完了",
            "地域ごとの上位9会場カテゴリを順位付け",
            "K-meansラベルを5つの地理的整合クラスターとマスターDataFrameに結合",
            "2種類のFoliumマップを作成：生の地区分布 + クラスターカラー出力",
          ],
        },
      ],
      descriptions: [
        "世界初のパデル向けAI自動審判システム。マルチカメラコンピュータビジョンパイプライン — ボール追跡・4プレイヤー骨格検出・コート境界認識を60fps・16ms以下の判定レイテンシで実現。20試合のテストデータセットで99.9%精度を検証。ソロ開発：フルスタック＋CVエンジニアリング。",
        "壊れたAmazonモニタリングSaaSを引き継ぎ、1週間以内に15分スクレイプサイクルを復旧。その後システム全体を再設計・再構築。ユーザー別ジョブ分離・最適化されたSQLクエリ・非同期スクレイピングエンジン・アラートルーティング（価格・タイトル・レビュー数・BuyBox・乗っ取り検知・MAP違反）を実装。現在も本番稼働中。",
        "建設見積担当者に45年分のRichardson見積基準価格データを提供するサブスクリプションSaaSの全開発を担当。レガシーDBの移行・サーバー構築・完全SPA開発（サブスクリプション認証・15以上のカテゴリ階層ナビ・動的フィルタリング価格表・見積リストワークフロー）を実現。",
        "エンタープライズ向け2モジュールSaaS。モジュール1：Salesforce・RainFocus・Zoom間のデータ転送を自動化する同期エンジン（手動CSV不要）。モジュール2：8種類のウィジェット（ドーナツ・棒グラフ・ゲージ・ツリーマップ・リーダーボード等）によるセルフサービス型分析ダッシュボード。",
        "不動産業者向けSMS CRM（業界1位評価）を救済・再構築。6名のサブコントラクターをリードして完全再構築。月100k以上のSMSを98.1%の配信率でハンドリング。リードスコアリング・DNC管理・スキップトレース・予約送信・ライブ分析ダッシュボードを備えた3パネルCRM。マルチイヤー案件。",
        "ユーザーがジャンル・BPM・キー・デュレーション・スタイルタグを設定してオリジナル音源を生成できるフルスタックAI音楽SaaS。Python/PyTorchでAI生成パイプラインを構築し、コミュニティExploreフィード・クリエイタープロフィール・サンプルパック・クレジット制度・4段階サブスクリプション（$0〜$49.99/月）・24-bit WAVエクスポートを実装。",
        "K-meansを用いてトロント100地区を会場特性でクラスタリングするエンドツーエンドのデータサイエンスパイプライン。WikipediaからWebスクレイピング・Foursquare API・地区ごとの上位9会場カテゴリ特徴量エンジニアリング・教師なしクラスタリングを実施し、2種類のインタラクティブFoliumマップで結果を可視化。",
      ],
    },
    contact: {
      title: "お問い合わせ",
      description:
        "以下の連絡先からご連絡ください。できる限り迅速にご返信いたします。",
    },
    contactForm: {
      title: "フォームに記入する",
      subtitle: "24時間以内にご返信いたします",
      name: "氏名",
      namePlaceholder: "お名前（フルネーム）",
      email: "メールアドレス",
      emailPlaceholder: "example@email.com",
      phone: "電話番号",
      phonePlaceholder: "+81 (XX) XXXX XXXX",
      company: "会社名",
      companyPlaceholder: "会社名（任意）",
      service: "ご希望のサービス",
      details: "プロジェクト詳細",
      detailsPlaceholder:
        "プロジェクトの詳細をご記入ください。内容・予算・スケジュールなどをお知らせいただければ幸いです。",
      send: "送信",
      sending: "送信中...",
      success: "送信しました。24時間以内にご返信いたします。",
      error: "送信に失敗しました。もう一度お試しいただくか、直接メールでご連絡ください。",
      bookMeeting: "ミーティングを予約",
      infoTitle: "お問い合わせ",
      infoDesc:
        "ご質問はお気軽にどうぞ。お好みの連絡方法でご連絡ください。できる限り迅速にご返信いたします。",
      sendEmail: "メールを送る",
      connect: "つながる",
      visit: "見る",
      location: "リモート — 世界対応",
      availabilityLabel: "対応時間",
      workingDays: "月 – 金",
      active: "対応中",
    },
  },
};

export default translations;
